// src/hooks/useAuth.ts
import { useState, useEffect } from 'react';
import { User } from 'oidc-client-ts';
import { authService } from '../auth/AuthService';

interface UseAuthReturn {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  signIn: (role?: "teacher" | "student") => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const initAuth = async () => {
      try {
        // 首先檢查 localStorage 中是否有用戶資料
        const userDataString = localStorage.getItem("oidc.user:https://id.hle.com.tw:js");
        if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            // 檢查 token 是否過期
            const expiresAt = userData.expires_at * 1000; // 轉換為毫秒
            const now = Date.now();
            
            if (expiresAt > now) {
              // Token 還沒過期，檢查並處理 UUID 名稱問題
              if (userData.profile) {
                // 優先使用 firstName + lastName
                if (userData.profile.firstName && userData.profile.lastName) {
                  userData.profile.name = `${userData.profile.lastName}${userData.profile.firstName}`;
                } else {
                  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userData.profile.name || '');

                  // 如果 name 是 UUID，嘗試使用其他欄位
                  if (isUUID && userData.profile.preferred_username) {
                    userData.profile.name = userData.profile.preferred_username;
                  }

                  // 處理 name 為陣列的情況
                  if (Array.isArray(userData.profile.name)) {
                    userData.profile.name = userData.profile.name[0] || "用戶";
                  }
                }
              }
              
              setUser(userData);
              setIsLoading(false);
              return;
            }
          } catch (e) {
            console.warn('解析 localStorage 用戶資料失敗:', e);
          }
        }
        
        // 如果 localStorage 沒有或過期，嘗試從 authService 獲取
        const currentUser = await authService.getUser();
        setUser(currentUser);
      } catch (error) {
        console.error('初始化認證失敗:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    initAuth();
  }, []);
  
  const signIn = async (role?: "teacher" | "student") => {
    try {
      await authService.signIn(role);
    } catch (error) {
      console.error('登入失敗:', error);
    }
  };
  
  const signOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
    } catch (error) {
      console.error('登出失敗:', error);
    }
  };
  
  return {
    isAuthenticated: user !== null && (!user.expired || Date.now() < (user.expires_at || 0) * 1000),
    user,
    isLoading,
    signIn,
    signOut
  };
};